import React, { useEffect, useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, TrendingUp, Award, Users as UsersIcon, Receipt, ChevronLeft, ChevronRight } from "lucide-react";
import { useStore } from "@/lib/storeContext";

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const formatItDate = (iso) => new Date(iso).toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

const MEDAL = ["🥇", "🥈", "🥉"];

export default function ReportOperators() {
  const { storeId } = useStore();
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/reports/operators?date=${date}${storeId ? `&store_id=${storeId}` : ""}`).then(r => setData(r.data))
      .catch(() => toast.error("Errore caricamento report"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [date, storeId]);

  const maxTotal = Math.max(1, ...(Array.isArray(data?.operators) ? data?.operators : []).map(o => o.total));

  return (
    <div className="p-8 lg:p-12">
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Report</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">Performance operatori</h1>
        <p className="text-velta-mute">Vendite e scontrini per ogni cassiere — perfetto per i premi di produttività.</p>
      </div>

      {/* Date nav */}
      <div className="bg-white border border-velta-border rounded-md p-4 mb-6 flex items-center justify-center gap-3">
        <button data-testid="report-prev-day" onClick={() => setDate(addDays(date, -1))} className="w-10 h-10 border border-velta-border rounded-md flex items-center justify-center hover:border-velta-ink"><ChevronLeft className="w-4 h-4" /></button>
        <button data-testid="report-today" onClick={() => setDate(todayISO())} className="px-4 h-10 border border-velta-border rounded-md text-sm hover:border-velta-ink">Oggi</button>
        <div className="flex items-center gap-2 px-3 h-10 bg-velta-bg border border-velta-border rounded-md">
          <Calendar className="w-4 h-4 text-velta-mute" />
          <input
            data-testid="report-date-picker"
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="bg-transparent font-mono outline-none"
          />
        </div>
        <div className="font-heading font-semibold capitalize ml-2" data-testid="report-current-date">{formatItDate(date)}</div>
        <button data-testid="report-next-day" onClick={() => setDate(addDays(date, 1))} className="w-10 h-10 border border-velta-border rounded-md flex items-center justify-center hover:border-velta-ink"><ChevronRight className="w-4 h-4" /></button>
      </div>

      {loading ? (
        <div className="text-center text-velta-mute py-12">Caricamento…</div>
      ) : !data || data.operators.length === 0 ? (
        <div className="bg-white border border-velta-border rounded-md p-12 text-center text-velta-mute">
          Nessuno scontrino chiuso in questa data.
        </div>
      ) : (
        <>
          {/* Totals header */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white border border-velta-border rounded-md p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1 inline-flex items-center gap-1"><UsersIcon className="w-3 h-3" /> Operatori attivi</div>
              <div data-testid="report-total-ops" className="font-mono font-bold text-3xl tracking-tighter">{data.totals.operators_count}</div>
            </div>
            <div className="bg-white border border-velta-border rounded-md p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1 inline-flex items-center gap-1"><Receipt className="w-3 h-3" /> Scontrini totali</div>
              <div data-testid="report-total-receipts" className="font-mono font-bold text-3xl tracking-tighter">{data.totals.receipts}</div>
            </div>
            <div className="bg-white border border-velta-border rounded-md p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1 inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Fatturato giornata</div>
              <div data-testid="report-total-amount" className="font-mono font-bold text-3xl tracking-tighter">{formatEUR(data.totals.total)}</div>
            </div>
          </div>

          {/* Operators ranking */}
          <div className="space-y-3">
            {(Array.isArray(data.operators) ? data.operators : []).map((op, idx) => {
              const pct = (op.total / maxTotal) * 100;
              return (
                <div key={op.operator_id} data-testid={`report-op-${idx}`} className="bg-white border border-velta-border rounded-md p-5">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="text-3xl">{MEDAL[idx] || <Award className="w-6 h-6 text-velta-mute inline" />}</div>
                      <div>
                        <div className="font-heading font-semibold text-lg leading-tight">{op.operator_name}</div>
                        <div className="text-xs text-velta-mute">{(op.first_at || "").slice(11, 16)} → {(op.last_at || "").slice(11, 16)}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-bold text-2xl tracking-tighter">{formatEUR(op.total)}</div>
                      <div className="text-xs text-velta-mute">{op.receipts} scontrini · media {formatEUR(op.avg_receipt)}</div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="h-2 bg-velta-bg rounded-full overflow-hidden mb-3">
                    <div
                      className="h-full bg-gradient-to-r from-[#2F9E77] to-[#1E2D5B] rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>

                  {/* Payments breakdown */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
                    {[
                      { k: "payment_cash", label: "Contanti", v: op.payment_cash },
                      { k: "payment_card", label: "Carta", v: op.payment_card },
                      { k: "payment_ticket", label: "Ticket", v: op.payment_ticket },
                      { k: "payment_mixed", label: "Misto", v: op.payment_mixed },
                    ].map(p => p.v > 0 && (
                      <div key={p.k} className="bg-velta-bg rounded-md px-3 py-2">
                        <div className="text-velta-mute">{p.label}</div>
                        <div className="font-mono font-bold">{formatEUR(p.v)}</div>
                      </div>
                    ))}
                  </div>

                  {/* Modes pies */}
                  {Object.keys(op.modes).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-velta-border flex flex-wrap gap-2">
                      <span className="text-xs text-velta-mute">Modalità:</span>
                      {Object.entries(op.modes).map(([m, count]) => (
                        <span key={m} className="text-xs font-mono uppercase tracking-widest bg-velta-bg border border-velta-border px-2 py-0.5 rounded">
                          {m} ({count})
                        </span>
                      ))}
                    </div>
                  )}

                  {op.fiscal_count > 0 && op.non_fiscal_count > 0 && (
                    <div className="mt-2 text-xs text-velta-mute">
                      {op.fiscal_count} fiscali · {op.non_fiscal_count} non fiscali
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
